import React, {
  FunctionComponent,
  useState,
  useEffect,
  useRef,
  ElementType,
} from 'react';
import { Table as SemanticTable, Menu, Icon } from 'semantic-ui-react';
import classnames from 'classnames';
import withStyles from 'react-jss';
import {
  map,
  pathOr,
  addIndex,
  splitEvery,
  compose,
  values,
  prop,
  path,
  sum,
  mean,
  descend,
  ascend,
  sortWith,
  toLower,
} from 'ramda';

import { ClassProp } from 'helpers/types';
import DownloadWidget from 'components/DownloadWidget';
import { Field } from 'components/DownloadWidget/DownloadWidget';

import styles from './Table.styles';

const mapIndexed = addIndex(map);

export type TableHeader = {
  source: Array<number | string> | Function;
  label: string;
  transform?: Function;
  sortable?: string[];
  defaultValue?: string;
  component?: {
    Element: ElementType | FunctionComponent;
    props?: { onClick?: Function; [key: string]: any };
  };
  calcMean?: boolean;
  justify?: 'left' | 'right' | 'center';
  skipTotal?: boolean;
  options?: Record<string, any>;
  width?: 'one' | 'two' | 'three' | 'four' | 'five' | 'ten';
};

export enum Direction {
  ASCENDING = 'ascending',
  DESCENDING = 'descending',
}

type Pagination = {
  page: number;
  hasPrevious: boolean;
  hasNext: boolean;
  loadMore: (direction: 'next' | 'previous') => void;
  pagesPerGroup: number;
};

type Styles = ClassProp<ReturnType<typeof styles>>;

export type SortDefaultColumn = {
  name: string;
  sortable: string[];
  direction?: Direction;
};

type Props = {
  tableHeader: TableHeader[];
  data: Record<string, unknown>[];
  classes: Styles;
  splitEveryArg?: number;
  itemsKey?: string;
  pagination?: Pagination;
  showTotals?: boolean;
  exportData?: boolean;
  csvFields?: Field[];
  sortDefaultColumn?: SortDefaultColumn;
  sortSecondaryColumn?: SortDefaultColumn;
  expandedViewComponent?: JSX.Element;
  selectedrowCallback?: (rowId: string) => void;
  selectedDataIdRow?: string;
};

const getSortedData = (
  isSameColumn: boolean,
  data: Record<string, unknown>[],
  direction: Direction,
  sortable: string[],
  secondarySortable: string[] = [],
) => {
  if (isSameColumn) {
    return data.reverse();
  }

  const sortDirection = (direction: Direction) =>
    direction === Direction.DESCENDING ? descend : ascend;

  const sortByCondition = compose(
    (value: string | number) =>
      typeof value === 'string' ? toLower(value) : value,
    pathOr('_', sortable),
  );

  const sortBySecondaryCondition = compose(
    (value: string | number) =>
      typeof value === 'string' ? toLower(value) : value,
    pathOr('_', secondarySortable),
  );

  return sortWith(
    [
      sortDirection(direction)(sortByCondition),
      sortDirection(direction)(sortBySecondaryCondition),
    ],
    data,
  );
};

const getDirection = (isSameColumn: boolean, direction?: Direction) => {
  if (isSameColumn && direction === Direction.ASCENDING) {
    return Direction.DESCENDING;
  }

  return Direction.ASCENDING;
};

const getFieldValueBySource = (
  { source, transform, defaultValue = 'N/A', component }: TableHeader,
  row: Record<string, unknown>,
) => {
  let fieldValue = defaultValue as any;

  if (Array.isArray(source)) {
    fieldValue = pathOr(fieldValue, source, row);
  }

  if (component) {
    const Element = component.Element;
    if (component.props) {
      const { onClick, ...props } = component.props;
      const finalProps = {
        ...props,
        onClick: onClick
          ? onClick(pathOr(fieldValue, source as string[], row))
          : null,
      };
      fieldValue = (
        <Element
          {...finalProps}
          source={source}
          record={row}
          value={path(['id'], row)}
        />
      );
    } else {
      fieldValue = (
        <Element
          record={row}
          {...component.props}
          source={source}
          value={path(['id'], row)}
        />
      );
    }
  }

  if (typeof source === 'function') {
    fieldValue = source(row);
  }

  if (transform) {
    fieldValue = transform(fieldValue);
  }

  return fieldValue;
};

const renderHeader = (
  direction: Direction,
  column: string,
  handleSort: (clickedColumn: string, sortable: string[]) => () => void,
  classes: Styles,
) =>
  map(({ label, sortable, justify = 'left', width }: TableHeader) => (
    <SemanticTable.HeaderCell
      key={label}
      width={width}
      sorted={column === label ? direction : undefined}
      onClick={!!sortable ? handleSort(label, sortable) : null}
      className={classnames(
        {
          notSortable: !sortable,
          [classes.justifyLeft]: justify === 'left',
          [classes.justifyRight]: justify === 'right',
          [classes.justifyCenter]: justify === 'center',
        },
        classes.tableHeader,
      )}
    >
      {label}
    </SemanticTable.HeaderCell>
  ));

const jobCellMapping = (row: Record<string, unknown>, classes: Styles) => ({
  label,
  width,
  justify = 'left',
  options,
  ...rest
}: TableHeader & any) => {
  const fieldValue: string = getFieldValueBySource(rest, row);
  return (
    <SemanticTable.Cell
      width={width}
      key={label}
      className={classnames(pathOr('', [options?.class], classes), {
        [classes.justifyLeft]: justify === 'left',
        [classes.justifyRight]: justify === 'right',
        [classes.justifyCenter]: justify === 'center',
      })}
    >
      {fieldValue}
    </SemanticTable.Cell>
  );
};

const renderFooter = (
  page: number,
  splitEveryArg: number,
  dataProp: Record<string, unknown>[],
  colSpan: number,
  onMenuClick: (
    index: number,
    hasMoreRecords: boolean,
    direction: 'next' | 'previous',
  ) => () => void,
  pagination: Pagination,
  classes: ClassProp<ReturnType<typeof styles>>,
) => {
  const splitData = splitEvery(splitEveryArg, dataProp);
  const hasPreviousPage = page > 0;
  const hasNextPage = page + 1 < splitData.length;
  const control = { nextPage: page + 1, previousPage: page - 1 };

  // don't show pagination in case we don't have enough data
  if (splitData.length === 1) {
    return null;
  }

  return (
    <SemanticTable.Row>
      <SemanticTable.HeaderCell colSpan={colSpan}>
        <Menu className={classes.pagination} floated="right" pagination>
          <Menu.Item
            disabled={!hasPreviousPage && !pagination.hasPrevious}
            as="a"
            icon
            onClick={onMenuClick(
              control.previousPage,
              hasPreviousPage,
              'previous',
            )}
          >
            <Icon name="chevron left" />
          </Menu.Item>

          {splitData.map((_, index) => (
            <Menu.Item
              active={index === page}
              onClick={onMenuClick(index, true, 'next')}
              key={index}
              as="a"
            >
              {index + 1 + pagination.page * pagination.pagesPerGroup}
            </Menu.Item>
          ))}

          <Menu.Item
            disabled={!hasNextPage && !pagination.hasNext}
            as="a"
            icon
            onClick={onMenuClick(control.nextPage, hasNextPage, 'next')}
          >
            <Icon name="chevron right" />
          </Menu.Item>
        </Menu>
      </SemanticTable.HeaderCell>
    </SemanticTable.Row>
  );
};

const sumField = (field: string) =>
  compose(sum, values, map<unknown, any>(prop(field)));

const calculateMean = (field: string) =>
  compose(mean, values, map<unknown, any>(prop(field)));

const getTotalRow = (
  tableHeader: TableHeader[],
  dataProp: Record<string, unknown>[],
) =>
  tableHeader.reduce((totalRow: Record<string, unknown>, curr: TableHeader) => {
    const key = pathOr('', ['source', 0], curr);
    const calcMean = pathOr(false, ['calcMean'], curr);
    const skipTotal = pathOr(false, ['skipTotal'], curr);
    const isNumber = typeof path([0, key], dataProp) === 'number';
    let value: number | string | null = '-';

    if (isNumber && !skipTotal) {
      if (calcMean) {
        value = calculateMean(key)(dataProp);
      } else {
        value = sumField(key)(dataProp);
      }
    }

    return {
      ...totalRow,
      [key]: value,
    };
  }, {});

const renderTotals = (
  tableHeader: TableHeader[],
  dataProp: Record<string, unknown>[],
  classes: Styles,
) => {
  const totalRow = getTotalRow(tableHeader, dataProp);

  return (
    <SemanticTable.Row>
      {map(jobCellMapping(totalRow, classes), tableHeader)}
    </SemanticTable.Row>
  );
};

const defaultPagination = {
  hasPrevious: false,
  hasNext: false,
  loadMore: () => {},
  page: 0,
  pagesPerGroup: 0,
};

type RowClicked = {
  showDetails: boolean;
  index: number;
  id: string | null;
};

const Table: FunctionComponent<Props> = ({
  classes,
  tableHeader,
  data: dataProp,
  itemsKey,
  splitEveryArg = 20,
  sortDefaultColumn,
  sortSecondaryColumn,
  pagination = defaultPagination,
  showTotals,
  exportData,
  csvFields,
  expandedViewComponent,
  selectedrowCallback,
  selectedDataIdRow,
}) => {
  const [data, setData] = useState<Record<string, unknown>[]>(dataProp);
  const [page, setPage] = useState<number>(0);
  const [column, setColumn] = useState<string>('');
  const [direction, setDirection] = useState<Direction>(Direction.ASCENDING);
  const tableRef = useRef<null>(null);
  const [rowClicked, setRowClicked] = useState<RowClicked>({
    showDetails: false,
    index: -1,
    id: '',
  });

  useEffect(() => {
    if (sortDefaultColumn && sortDefaultColumn.sortable.length > 0) {
      const {
        name,
        sortable,
        direction = Direction.ASCENDING,
      } = sortDefaultColumn;
      setData(
        getSortedData(
          false,
          dataProp,
          direction,
          sortable,
          sortSecondaryColumn?.sortable,
        ),
      );
      setColumn(name);
    } else {
      setData(data);
    }
    setDirection(direction);
  }, [pagination.page, dataProp]);

  const onMenuClick = (
    index: number,
    hasMoreRecords: boolean,
    direction: 'next' | 'previous',
  ) => () => {
    if (!hasMoreRecords) {
      pagination.loadMore(direction);
      setPage(direction === 'next' ? 0 : pagination.pagesPerGroup - 1);
    } else {
      setPage(index);
    }
  };

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const rowIndex = parseInt(
      event.currentTarget.getAttribute('data-index') ?? '0',
    );

    const rowId = event.currentTarget.getAttribute('data-id') ?? '';

    if (rowIndex === rowClicked.index) {
      setRowClicked({
        showDetails: false,
        index: -1,
        id: rowId,
      });
      return;
    }

    setRowClicked({
      index: rowIndex,
      showDetails: true,
      id: rowId,
    });

    selectedrowCallback && selectedrowCallback(rowId);
  };

  const renderBody = (
    tableHeader: TableHeader[],
    itemsKey = '',
    classes: Styles,
  ) =>
    mapIndexed((bodyRow: unknown, index: number) => {
      const dataId = pathOr(index, itemsKey.split('.'), bodyRow);
      return (
        <React.Fragment key={dataId}>
          <SemanticTable.Row
            onClick={handleRowClick}
            data-index={index}
            data-id={dataId}
            className={classnames(
              selectedDataIdRow === dataId ? classes.selectedRow : '',
              { [classes.expandableRow]: expandedViewComponent },
            )}
          >
            {map(
              jobCellMapping(bodyRow as Record<string, unknown>, classes),
              tableHeader,
            )}
          </SemanticTable.Row>
          {expandedViewComponent &&
            rowClicked.showDetails &&
            index === rowClicked.index && (
              <SemanticTable.Row>
                <SemanticTable.Cell
                  className={classes.supplementalRow}
                  colSpan={12}
                >
                  {expandedViewComponent}
                </SemanticTable.Cell>
              </SemanticTable.Row>
            )}
        </React.Fragment>
      );
    });

  const handleSort = (clickedColumn: string, sortable: string[]) => () => {
    const isSameColumn = column === clickedColumn;

    setData(
      getSortedData(
        isSameColumn,
        data as Record<string, unknown>[],
        direction,
        sortable,
      ),
    );
    setColumn(isSameColumn ? column : clickedColumn);
    setDirection(getDirection(isSameColumn, direction));
  };

  const hasPagination = !!pagination;

  return (
    <div ref={tableRef} className={classes.ref}>
      <SemanticTable
        selectable
        sortable={!!sortDefaultColumn || !!sortSecondaryColumn}
        className={classes.table}
      >
        <SemanticTable.Header>
          {exportData && (
            <SemanticTable.Row>
              <SemanticTable.HeaderCell
                className={classes.downloadCell}
                colSpan={tableHeader.length}
              >
                <DownloadWidget
                  nodeRef={tableRef}
                  className={classes.download}
                  csvName={itemsKey || 'data'}
                  data={[...dataProp, getTotalRow(tableHeader, dataProp)]}
                  options={['CSV']}
                  fields={csvFields}
                />
              </SemanticTable.HeaderCell>
            </SemanticTable.Row>
          )}

          <SemanticTable.Row>
            {renderHeader(direction, column, handleSort, classes)(tableHeader)}
          </SemanticTable.Row>
        </SemanticTable.Header>

        <SemanticTable.Body className={classes.tableBody}>
          {renderBody(
            tableHeader,
            itemsKey,
            classes,
          )(
            hasPagination
              ? (data.slice(
                  page * splitEveryArg,
                  (page + 1) * splitEveryArg,
                ) as any)
              : data,
          )}
        </SemanticTable.Body>

        {hasPagination && (
          <SemanticTable.Footer fullWidth>
            {showTotals && renderTotals(tableHeader, dataProp, classes)}

            {renderFooter(
              page,
              splitEveryArg,
              dataProp,
              tableHeader.length,
              onMenuClick,
              pagination,
              classes,
            )}
          </SemanticTable.Footer>
        )}
      </SemanticTable>
    </div>
  );
};

export default withStyles(styles)(Table);
